import { h, clear } from './dom.js'
import { on, emit } from '../lib/bus.js'
import { state, selectMove, setStamp, clearStamp, replaceStamps } from '../lib/state.js'
import { formatTime, parseTime } from '../lib/format.js'
import { violations } from '../lib/timeline.js'
import { toast } from './toast.js'
import { openShiftDialog } from './shiftDialog.js'
import { figurine } from './notation.js'

export function mountMoveList(parent) {
  const meta = h('div.score-meta', { hidden: true })
  const listEl = h('ol.score-rows')
  const empty = h(
    'div.score-empty',
    {},
    h('p', {}, 'No game loaded.'),
    h('p.hint', {}, 'Paste a PGN in the Game panel to fill this scoresheet.'),
  )
  // One-tap escape hatch for auto-filled guide times — no dialog, they're
  // regenerable from the Video panel. Sits at the top so it's reachable on
  // phones without scrolling past the whole move list.
  const scaffoldBar = h(
    'div.scaffold-banner',
    { hidden: true },
    h('span', {}, 'Guide times pre-filled'),
    h(
      'button.btn.ghost',
      {
        type: 'button',
        onClick: () => {
          replaceStamps([])
          toast('Guide times cleared — tag each move as it happens')
        },
      },
      'Clear all',
    ),
  )
  const root = h(
    'aside.scoresheet',
    {},
    h('header.score-head', {}, h('h2.card-title', {}, 'Scoresheet'), meta),
    scaffoldBar,
    empty,
    listEl,
  )

  let rows = [] // row elements by move index
  let liveIndex = -1

  function renderMeta() {
    const hd = state.pgn.headers
    clear(meta)
    if (!state.pgn.moves.length) {
      meta.hidden = true
      return
    }
    meta.hidden = false
    const players = `${hd.White ?? 'White'} — ${hd.Black ?? 'Black'}`
    meta.appendChild(h('div.score-players', {}, players))
    const detail = [hd.Event, hd.Date, hd.Result].filter(Boolean).join(' · ')
    if (detail) meta.appendChild(h('div.score-detail', {}, detail))
  }

  function buildRows() {
    clear(listEl)
    rows = state.pgn.moves.map((move, i) => {
      const row = h(
        'li.move-row',
        { dataset: { index: i } },
        h('span.move-no', {}, move.color === 'w' ? `${move.moveNumber}.` : `${move.moveNumber}…`),
        h('span.move-san', {}, figurine(move.san)),
        h(
          'span.row-actions',
          {},
          h('button.icon-btn', { type: 'button', dataset: { act: 'set' }, title: 'Set to current video time' }, '⏺'),
          h('button.icon-btn', { type: 'button', dataset: { act: 'shift' }, title: 'Shift timestamps from here…' }, '⇄'),
          h('button.icon-btn', { type: 'button', dataset: { act: 'clear' }, title: 'Clear timestamp' }, '✕'),
        ),
        h('button.stamp-chip', { type: 'button', title: 'Click to edit timestamp' }, '—'),
      )
      listEl.appendChild(row)
      return row
    })
    empty.hidden = rows.length > 0
    listEl.hidden = rows.length === 0
    updateStamps()
    updateSelection(true)
  }

  function updateStamps() {
    scaffoldBar.hidden = !(state.scaffolded && state.stamps.some((s) => s != null))
    const bad = new Set(violations(state.stamps))
    state.stamps.forEach((stamp, i) => {
      const row = rows[i]
      if (!row) return
      const chip = row.querySelector('.stamp-chip')
      const tagged = stamp != null
      row.classList.toggle('tagged', tagged)
      row.classList.toggle('warn', bad.has(i))
      chip.textContent = tagged ? formatTime(stamp) : '—'
      row.querySelector('[data-act="clear"]').hidden = !tagged
      row.title = bad.has(i) ? 'Out of order: this timestamp is earlier than a previous move' : ''
    })
  }

  function updateSelection(reveal) {
    rows.forEach((row, i) => row.classList.toggle('selected', i === state.selection))
    if (reveal && rows[state.selection]) {
      rows[state.selection].scrollIntoView({ block: 'nearest', behavior: motionSafe() })
    }
  }

  function updateLive() {
    const index = state.playback.activeIndex
    if (index === liveIndex) return
    if (rows[liveIndex]) rows[liveIndex].classList.remove('live')
    liveIndex = index
    if (rows[liveIndex]) {
      rows[liveIndex].classList.add('live')
      if (state.playback.playing) {
        rows[liveIndex].scrollIntoView({ block: 'nearest', behavior: motionSafe() })
      }
    }
  }

  function startInlineEdit(index, chip) {
    const current = state.stamps[index]
    const input = h('input.stamp-edit', {
      value: current != null ? formatTime(current) : '',
      placeholder: 'm:ss.cc',
      spellcheck: false,
    })
    const commit = () => {
      const text = input.value.trim()
      restore()
      if (!text) {
        if (current != null) clearStamp(index)
        return
      }
      const seconds = parseTime(text)
      if (seconds == null) {
        toast(`Couldn't read "${text}" as a time — use m:ss.cc or plain seconds`, { kind: 'warn' })
        return
      }
      setStamp(index, seconds)
      emit('seek', { time: seconds })
    }
    const restore = () => {
      input.replaceWith(chip)
    }
    input.addEventListener('keydown', (e) => {
      e.stopPropagation()
      if (e.key === 'Enter') commit()
      if (e.key === 'Escape') restore()
    })
    input.addEventListener('blur', () => restore())
    chip.replaceWith(input)
    input.focus()
    input.select()
  }

  listEl.addEventListener('click', (e) => {
    const row = e.target.closest('.move-row')
    if (!row) return
    const index = Number(row.dataset.index)
    const action = e.target.closest('[data-act]')?.dataset.act
    if (action === 'set') {
      selectMove(index)
      if (!state.video.url) {
        toast('Load a video first — the timestamp comes from the playhead', { kind: 'warn' })
        return
      }
      setStamp(index, state.playback.time)
      selectMove(Math.min(index + 1, state.pgn.moves.length - 1), { reveal: true })
      return
    }
    if (action === 'clear') {
      clearStamp(index)
      return
    }
    if (action === 'shift') {
      selectMove(index)
      openShiftDialog(index)
      return
    }
    if (e.target.closest('.stamp-chip')) {
      selectMove(index)
      startInlineEdit(index, e.target.closest('.stamp-chip'))
      return
    }
    selectMove(index)
    const stamp = state.stamps[index]
    if (stamp != null) emit('seek', { time: stamp })
  })

  on('pgn', () => {
    renderMeta()
    buildRows()
    liveIndex = -1
  })
  on('stamps', updateStamps)
  on('selection', (payload) => updateSelection(payload?.reveal))
  on('time', updateLive)

  parent.appendChild(root)
  return root
}

function motionSafe() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
}
